/**
 * main.ts — Hearth Entry Point
 *
 * The front door. Everything that needs to exist before the first frame is
 * set up here: the canvas, the DPI scaling, the input system, the game state,
 * the loop. After this file runs, the world is alive and the rest is just
 * frames ticking past like pages of a story.
 *
 * Loaded as:  <script type="module" src="dist/main.js"></script>
 *
 * Boot sequence:
 *   1. Find the canvas and get its 2D context
 *   2. Configure high-DPI rendering (buffer size × devicePixelRatio, up to 2×)
 *   3. Measure the canvas and size it correctly
 *   4. Create the initial game state from those dimensions
 *   5. Create the input system (mouse, touch, keyboard, chat)
 *   6. Register the Page Visibility API handler
 *   7. Start the game loop — the pulse begins
 */

import { createGameState, beginTransition, createStarterRoom } from "./engine/state.js";
import { GameLoop, registerVisibilityHandler } from "./engine/loop.js";
import { InputSystem } from "./engine/input.js";
import { AudioEngine } from "./audio/engine.js";
import { initMobilePlatform } from "./engine/mobile.js";
import { affectMLP } from "./aeterna/neural/affect-mlp.js";

// ─── Bootstrap ────────────────────────────────────────────────────────────────

(function boot(): void {
  // ── Welcome ────────────────────────────────────────────────────────────────
  console.log(
    "%c🏠 Hearth",
    "color: #c4956a; font-size: 24px; font-weight: bold"
  );
  console.log(
    "%cWelcome home.",
    "color: #8a7a6a; font-size: 14px"
  );

  // ── Canvas ─────────────────────────────────────────────────────────────────
  const canvas = document.getElementById("hearth") as HTMLCanvasElement;
  if (!canvas) {
    console.error("[Hearth] Could not find #hearth canvas element.");
    return;
  }

  const ctx = canvas.getContext("2d")!;
  if (!ctx) {
    console.error("[Hearth] Could not get 2D context from canvas.");
    return;
  }

  // ── High-DPI Setup ─────────────────────────────────────────────────────────
  // dpr is re-read on every resize. Browser zoom (Ctrl+/Cmd+/-) changes both
  // viewport CSS pixels AND devicePixelRatio in the same tick — capturing dpr
  // once at boot leaves the backing buffer mis-scaled after the first zoom.
  // We cap at 2× — beyond that the memory cost outweighs the sharpness gain.
  // All drawing is done in logical pixels; ctx is pre-scaled per resize so
  // callers never need to think about it.
  const DPR_CAP = 2;
  const getDpr = (): number => Math.min(window.devicePixelRatio || 1, DPR_CAP);

  // Read logical dimensions from the layout box. getBoundingClientRect gives
  // sub-pixel precision; clientWidth rounds. We round to integer for the
  // backing buffer (canvas.width must be int) but keep the CSS size flexible.
  const measure = (): { w: number; h: number } => {
    const rect = canvas.getBoundingClientRect();
    return {
      w: rect.width  || canvas.clientWidth  || window.innerWidth,
      h: rect.height || canvas.clientHeight || window.innerHeight,
    };
  };

  let resizePending = false;
  function applyResize(): void {
    resizePending = false;
    const dpr = getDpr();
    const { w: logicalW, h: logicalH } = measure();

    // Buffer size = logical × dpr for crisp rendering on retina displays
    // and after browser zoom-in (which raises devicePixelRatio).
    const bufW = Math.max(1, Math.round(logicalW * dpr));
    const bufH = Math.max(1, Math.round(logicalH * dpr));

    // Skip the assignment if nothing changed — assigning canvas.width clears
    // the buffer and causes a one-frame black flash even when redundant.
    if (canvas.width !== bufW)  canvas.width  = bufW;
    if (canvas.height !== bufH) canvas.height = bufH;

    // Pin CSS size to the measured logical dimensions. With width:100%/height:100%
    // in CSS this is usually a no-op, but writing it explicitly guards against
    // edge cases (rotated devices, fullscreen toggle) where the cascade lags.
    canvas.style.width  = `${logicalW}px`;
    canvas.style.height = `${logicalH}px`;

    // Re-apply the dpr transform every resize. Setting canvas.width/height
    // resets the transform matrix to identity, so this must come AFTER.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Keep state in sync with the new logical dimensions
    if (state) {
      const oldW = state.canvasWidth;
      const oldH = state.canvasHeight;
      state.canvasWidth  = logicalW;
      state.canvasHeight = logicalH;

      // Re-center camera on resize so the room doesn't drift off-screen.
      // Shift camera by half the dimension delta to keep the focal point centred.
      if (state.scene === "room" && oldW > 0 && oldH > 0) {
        const dw = (logicalW - oldW) / 2;
        const dh = (logicalH - oldH) / 2;
        state.camera.x       += dw;
        state.camera.y       += dh;
        state.camera.targetX += dw;
        state.camera.targetY += dh;
      }
    }

    // Re-arm the dpr-change watcher with the new resolution. matchMedia
    // listens to a specific dppx value, so when dpr changes we need a fresh
    // query targeting the new value to catch the next change.
    armDprWatcher(dpr);
  }

  // Coalesce multiple resize triggers in the same frame (ResizeObserver +
  // visualViewport + window.resize can all fire within one tick on zoom).
  function scheduleResize(): void {
    if (resizePending) return;
    resizePending = true;
    requestAnimationFrame(applyResize);
  }

  // dpr-change watcher. matchMedia(`(resolution: Xdppx)`) flips when the
  // browser's effective resolution changes — the canonical signal for
  // Ctrl+/Cmd+/- zoom. We re-create the listener after each change because
  // matchMedia queries are pinned to a specific value.
  let dprMql: MediaQueryList | null = null;
  let dprMqlHandler: ((e: MediaQueryListEvent) => void) | null = null;
  function armDprWatcher(currentDpr: number): void {
    if (dprMql && dprMqlHandler) {
      dprMql.removeEventListener("change", dprMqlHandler);
    }
    // Use the raw devicePixelRatio (uncapped) so we still detect zoom changes
    // above the DPR_CAP — the cap only affects buffer size, not detection.
    const raw = window.devicePixelRatio || 1;
    dprMql = window.matchMedia(`(resolution: ${raw}dppx)`);
    dprMqlHandler = () => scheduleResize();
    dprMql.addEventListener("change", dprMqlHandler);
    // currentDpr is currently unused but kept in the signature so callers
    // document the post-resize dpr they're arming against.
    void currentDpr;
  }

  // Measure logical dimensions before creating state so the camera starts
  // centred correctly on first frame
  const initial = measure();
  const initialW = initial.w;
  const initialH = initial.h;

  // ── Game State ─────────────────────────────────────────────────────────────
  const state = createGameState(initialW, initialH);

  // Now that state exists, run the first resize synchronously so the first
  // rendered frame already has the correct buffer + transform + state dims.
  applyResize();

  // ── Input System ───────────────────────────────────────────────────────────
  // InputSystem attaches its own listeners to the canvas and to window.
  // The chat input element (#chat-input) is looked up internally.
  const input = new InputSystem(canvas);

  // ── Audio Engine ─────────────────────────────────────────────────────────
  // Created dormant — requires user gesture to activate (browser policy).
  const audio = new AudioEngine();

  // ── Visibility Handler ─────────────────────────────────────────────────────
  // Marks state.tabVisible false when the player switches tabs, so the
  // game loop can cap dt on return and avoid physics explosions.
  registerVisibilityHandler(state);

  // ── Mobile Platform Hardening ──────────────────────────────────────────────
  // Wake lock, back button interception, visual viewport tracking, service
  // worker registration, install prompt capture, network status, reduced
  // motion detection — everything that makes a phone feel native.
  const mobile = initMobilePlatform(state);
  (state as unknown as Record<string, unknown>).mobile = mobile;

  // ── Resize Triggers ────────────────────────────────────────────────────────
  // Multiple sources can change the canvas's effective size; we listen to all
  // of them and let scheduleResize() coalesce into one rAF-aligned apply.
  //
  //   1. ResizeObserver on the canvas — most reliable for layout-driven size
  //      changes (CSS reflow, parent resize, fullscreen toggle). Fires for
  //      element-level changes that window.resize doesn't catch.
  //   2. window resize — desktop window drag, mobile rotation, browser zoom
  //      on most engines.
  //   3. visualViewport resize — mobile virtual-keyboard show/hide and pinch
  //      zoom. innerWidth doesn't track these.
  //   4. matchMedia(resolution) — browser zoom (Ctrl/Cmd +/-) when only the
  //      DPR changes without a window-size delta. armDprWatcher re-attaches
  //      with the new dppx value after each apply.
  //   5. orientationchange — older Safari fires this before resize on rotate.

  if (typeof ResizeObserver !== "undefined") {
    const ro = new ResizeObserver(() => scheduleResize());
    ro.observe(canvas);
  }
  window.addEventListener("resize", scheduleResize, { passive: true });
  window.addEventListener("orientationchange", scheduleResize, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", scheduleResize, { passive: true });
    window.visualViewport.addEventListener("scroll", scheduleResize, { passive: true });
  }
  // armDprWatcher was already called once inside applyResize() — that's our
  // initial subscription. Subsequent re-arming happens at the end of each apply.

  // ── Title screen: click/tap anywhere to enter ─────────────────────────────
  // The title renderer draws the cozy firelit room and the "click to enter"
  // prompt. Any interaction while on the title triggers the fade transition
  // to the starter room. The game loop handles the actual crossfade.
  function handleTitleClick(): void {
    if (state.scene === "title") {
      audio.resume();  // First user gesture — wake up Web Audio
      audio.playTitleAmbient(); // Warm pad + fire crackle starts immediately
      beginTransition(state, "character_create");
    }
  }

  canvas.addEventListener("click", handleTitleClick);
  canvas.addEventListener("touchend", (e: TouchEvent) => {
    if (state.scene === "title") {
      e.preventDefault();
      handleTitleClick();
    }
  }, { passive: false });

  // ── Game Loop ──────────────────────────────────────────────────────────────
  const loop = new GameLoop(canvas, ctx, state, input, audio);
  loop.start();

  // ── Affect MLP — load binary weights for neural affect classification ──────
  // Fire-and-forget: the game loop starts immediately. When the binary arrives
  // (~265KB), affectMLP.isLoaded() becomes true and CharCNN automatically
  // switches to the MLP path. Falls back to lexicon+ConvNet if fetch fails.
  fetch("/affect-mlp.bin")
    .then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.arrayBuffer();
    })
    .then(buf => {
      const ok = affectMLP.loadFromBuffer(buf);
      if (ok) {
        console.log("%c[Aeterna] Affect MLP loaded — neural affect signal active",
          "color: #c4956a; font-style: italic;");
      } else {
        console.warn("[Aeterna] Affect MLP binary parse failed — using lexicon fallback");
      }
    })
    .catch(e => {
      console.warn("[Aeterna] Affect MLP unavailable — using lexicon fallback", e);
    });

  // ── Dev helpers (localhost only) ───────────────────────────────────────────
  if (location.hostname === "localhost" || location.hostname === "127.0.0.1") {
    (window as unknown as Record<string, unknown>).__hearth = {
      state,
      input,
      loop,
      audio,
      goToRoom: () => {
        if (!state.currentRoom) state.currentRoom = createStarterRoom(state.player.id);
        beginTransition(state, "room");
      },
      goToTitle: () => beginTransition(state, "title"),
      metrics: () => loop.metrics,
    };
    console.info(
      "%cHearth dev mode. window.__hearth available.",
      "color: #c4956a; font-style: italic;"
    );
  }
})();
